import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getChatControlData,
  setChatDialogOpen,
  setVideoChatOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import { getVideoChatControlData } from 'components/ChatDialog/store/slices/videoChatSlice';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { loadVideoChatUsers } from 'components/ChatDialog/store/thunks/loadVideoChatUsers';
import {
  showChatNotification,
  showVideoCallNotification,
} from 'utils/browserNotifications';

/**
 * Custom hook to handle chat and video call notifications
 * Listens to notifyMessage from Redux and displays browser notifications
 */
export const useChatNotifications = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const { notifyMessage, threads } = useSelector(getChatControlData);
  const videoChatUsers = useSelector(getVideoChatControlData).users;

  useEffect(() => {
    if (!notifyMessage) return;

    // Only try to show notification if permission is already granted
    if (Notification.permission === 'granted') {
      const messageType = notifyMessage.type.split('#')[0];

      if (messageType === 'MESSAGE') {
        showChatNotification(notifyMessage, () => {
          // On notification click, open the chat thread
          if (!notifyMessage) return;
          const thread = threads[notifyMessage.threadId];
          if (!thread) return;
          dispatch(setThreadWithUsers({ thread }));
          dispatch(setChatDialogOpen(true));
        });
      } else if (messageType === 'VIDEOCHAT_SESSION') {
        let sender = videoChatUsers[notifyMessage.userId];

        const handleVideoCallNotification = (senderName) => {
          showVideoCallNotification(
            `${senderName} ${t('chat.is_calling', 'is calling')}`,
            () => {
              if (!notifyMessage) return;
              const thread = threads[notifyMessage.threadId];
              if (!thread) return;
              dispatch(setThreadWithUsers({ thread }));
              dispatch(setChatDialogOpen(true));
              dispatch(setVideoChatOpen(true));
            }
          );
        };

        if (sender && sender.firstName) {
          handleVideoCallNotification(sender.firstName);
        } else {
          dispatch(loadVideoChatUsers([notifyMessage.userId])).then(
            (result) => {
              handleVideoCallNotification(
                result.payload[notifyMessage.userId]?.firstName || ''
              );
            }
          );
        }
      }
    }
  }, [notifyMessage, threads, videoChatUsers, dispatch, t]);
};
